import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { LabelSection, LabelToggle, MasterSourceSelect, Section, Separator, ToggleCard } from '@Components';
import { ConfigOsaCalculation } from '@Models';

interface OsaCalculationSectionProps {
  data: ConfigOsaCalculation;
  readOnly?: boolean;
}

const OsaCalculationSection = ({ data, readOnly = true }: OsaCalculationSectionProps) => {
  const { t } = useTranslation();

  const valuesDaysSales = [
    {
      value: 'BopsRecommended',
      label: t('ConfigurationPolicy.OSA.BopsRecommended'),
      description: t('ConfigurationPolicy.OSA.BopsRecommendedDesc'),
    },
    {
      value: 'Provided',
      label: t('ConfigurationPolicy.OSA.Provided'),
      description: t('ConfigurationPolicy.OSA.ProvidedDesc'),
    },
  ];

  const probabilityOptions = [
    {
      value: 'Passive',
      label: t('ConfigurationPolicy.OSA.Passive'),
      tooltip: '5%',
    },
    {
      value: 'Average',
      label: t('ConfigurationPolicy.OSA.Average'),
      tooltip: '10%',
    },
    {
      value: 'Aggressive',
      label: t('ConfigurationPolicy.OSA.Aggressive'),
      tooltip: '15%',
    },
  ];

  return (
    <div className="space-y-6 mt-10">
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.OSA.DaysSinceLastSalesThreshold')}
            description={t('ConfigurationPolicy.OSA.DaysSinceLastSalesThresholdDesc')}
          />
        }
        right={
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {valuesDaysSales.map(({ label, description, value }) => {
                const isDataSelected = value === data.DaysSinceLastSalesThreshold.Selection;
                return (
                  <ToggleCard key={`${value}days`} selected={isDataSelected} readOnly={readOnly}>
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <LabelToggle label={label} />
                      </div>
                      <p className={cn('text-sm text-brand-300', isDataSelected ? 'text-brand-500' : 'text-gray-700')}>
                        {description}
                      </p>
                    </div>
                  </ToggleCard>
                );
              })}
            </div>
            {data?.DaysSinceLastSalesThreshold?.Master && (
              <MasterSourceSelect
                value={{
                  MasterSource: data.DaysSinceLastSalesThreshold.Master.Source,
                  MasterColumnName: data.DaysSinceLastSalesThreshold.Master.MasterColumnName,
                  DefaultValue: data.DaysSinceLastSalesThreshold.Master.DefaultValue,
                }}
                onChange={() => {}}
                readOnly={readOnly}
                defaultLabel={t('ConfigurationPolicy.OSA.DefaultValueDays')}
              />
            )}
          </div>
        }
      />
      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.OSA.SalesSensitivityProbability')}
            description={t('ConfigurationPolicy.OSA.SalesSensitivityProbabilityDesc')}
          />
        }
        right={
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {valuesDaysSales.map(({ label, value, description }) => {
                const isDataSelected = value === data.SalesSensitivityProbability?.Selection;
                return (
                  <ToggleCard key={`${value}sales`} selected={isDataSelected} readOnly={readOnly}>
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <LabelToggle label={label} />
                      </div>
                      <p className={cn('text-sm text-brand-300', isDataSelected ? 'text-brand-500' : 'text-gray-700')}>
                        {description}
                      </p>
                    </div>
                  </ToggleCard>
                );
              })}
            </div>
            {data?.SalesSensitivityProbability?.ProbabilityOption && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {probabilityOptions.map(({ label, value, tooltip }) => {
                  const isDataSelected = value === data.SalesSensitivityProbability.ProbabilityOption;
                  return (
                    <ToggleCard key={value} selected={isDataSelected} readOnly={readOnly} tooltip={tooltip}>
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-2">
                          <LabelToggle label={label} />
                        </div>
                      </div>
                    </ToggleCard>
                  );
                })}
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default OsaCalculationSection;
